import { useCallback } from 'react';
import { DrawableAudioPlayer } from 'utils/audio/types';
import { PlaybackState } from '../../types';

interface UsePlaybackControlsConfig {
  loadedUnitId: string | undefined;
  onInteraction?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  playbackState: PlaybackState;
  player: DrawableAudioPlayer | undefined;
}

interface UsePlaybackControlsResult {
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
}

export default function usePlaybackControls({
  loadedUnitId,
  onInteraction,
  onPause,
  onPlay,
  playbackState,
  player,
}: UsePlaybackControlsConfig): UsePlaybackControlsResult {
  const handlePlay = useCallback(() => {
    if (!player || !loadedUnitId) return;
    onInteraction?.();
    if (playbackState !== 'playing') {
      onPlay?.();

      if (playbackState === 'ended') {
        player.seek(0);
      }

      // info can be found all over the web. here's one link https://www.reddit.com/r/webdev/comments/71nkym/safari_11_has_a_major_change_to_web_audio_api/
      // the policy for playback starting with Safari 11 is that media elements need
      // to be interacted with before they can be played.  for our app, this means that
      // player.play() has to be called here and not, for example, in an effect
      // hook when the `playing` state changes.  The play() call must be made in the
      // event handler in order for Safari to grant permission to begin playback.  Once that
      // click gesture is registered, audio can be played and paused programmatically
      player?.play();
    }
  }, [loadedUnitId, onInteraction, onPlay, playbackState, player]);

  const handlePause = useCallback(() => {
    if (!player || !loadedUnitId) return;
    onInteraction?.();
    if (playbackState === 'playing') {
      onPause?.();
      player.pause();
    }
  }, [loadedUnitId, onInteraction, onPause, playbackState, player]);

  const handleTogglePlay = useCallback(() => {
    if (playbackState === 'playing') {
      handlePause();
    } else {
      handlePlay();
    }
  }, [handlePause, handlePlay, playbackState]);

  return {
    pause: handlePause,
    play: handlePlay,
    togglePlay: handleTogglePlay,
  };
}
